<template>
    <!-- v-show and not v-if: the scroller is bound to these elements once, for as long as the bar lives -->
    <div class="article-attribute-filter" ref="viewportElement" v-show="availableAttributes.length > 0">
        <div class="slidable dragable" ref="slidableElement">
            <button
                v-for="attribute in availableAttributes"
                :key="attribute.id"
                type="button"
                class="filter-attribute"
                :class="{
                    exclude: attribute.effect === AttributeEffect.Exclude,
                    active: isActive(attribute),
                }"
                @click="toggle(attribute)"
            >
                <i
                    v-if="attribute.effect === AttributeEffect.Exclude && !isActive(attribute)"
                    class="fas fa-minus-circle"
                ></i>
                <span class="name">{{ attributeName(attribute) }}</span>
                <i v-if="isActive(attribute)" class="fal fa-times"></i>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { MomentumScroller } from "@/app/ui/MomentumScroller";
    import type { ICustomerArticle, ICustomerArticleAttribute } from "@/data/model/Customer";
    import { AttributeDisplayType, AttributeEffect } from "@/data/model/Enums";
    import { useArticleAttributesStore } from "@/data/stores/articleAttribute";
    import { Format } from "@/data/util/Format";
    import { hasAttributeDisplayType } from "@/shared/helpers/AttributeHelper";
    import { clientLanguage } from "@/shared/localization/language";
    import { DomUtil } from "@/shared/ui/Helper";
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

    const props = defineProps<{
        items: Array<ICustomerArticle>;
    }>();

    const emit = defineEmits<{
        selectionChanged: [selection: { includeIds: Array<string>; excludeIds: Array<string> }];
    }>();

    const attributeStore = useArticleAttributesStore();

    const activeIds = ref<Array<string>>([]);
    // set by the host right before it opens a category the current selection is meant to survive
    let keepSelectionOnNextChange = false;

    const viewportElement = ref<HTMLElement | null>(null);
    const slidableElement = ref<HTMLElement | null>(null);
    // dragged by finger and by mouse alike, like the category slider above it - native overflow scrolled by touch
    // only, and with the scrollbar hidden a mouse had no way to reach the buttons past the edge
    let scroller: MomentumScroller | undefined;
    // the draggable width follows both boxes: the row changes with the buttons - another category offers others,
    // "Ordered before" joins once the orders are in, a web font that loads late widens them - and the viewport with a
    // rotation. showing the bar counts as a resize too
    let resizeObserver: ResizeObserver | undefined;

    onMounted(() => {
        scroller = new MomentumScroller($(slidableElement.value!), {
            axis: "x",
            restrictTo: $(viewportElement.value!),
        });

        resizeObserver = new ResizeObserver(() => updateScrollBounds());
        resizeObserver.observe(viewportElement.value!);
        resizeObserver.observe(slidableElement.value!);
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
        resizeObserver = undefined;
        scroller?.destroy();
        scroller = undefined;
    });

    // the store holds the enabled attributes of the whole location in the order every frontend renders
    // them in, so the order and the enabled check both come from there
    const filterAttributes = computed(() =>
        attributeStore.orderedAttributes.filter((attribute) =>
            hasAttributeDisplayType(attribute.displayType, AttributeDisplayType.Filter)
        )
    );

    // only the attributes present on the items of the current offer category, so the bar always shows
    // what the category being looked at can be filtered by
    const availableAttributes = computed(() => {
        // the offer knows the attributes linked to an article, the store also has "Ordered before", which no article is
        // linked to and which it derives from the orders of the user
        const presentIds = new Set(
            props.items.flatMap((item) => [
                ...item.attributeIds,
                ...attributeStore.filterAttributesForArticle(item.articleId).map((c) => c.id),
            ])
        );

        return filterAttributes.value.filter((attribute) => presentIds.has(attribute.id));
    });

    // drop selections that are not offered anymore, otherwise they would filter out everything
    watch(availableAttributes, (attributes) => {
        // the host is opening a category the selection should follow into, so this one change keeps it
        if (keepSelectionOnNextChange) {
            keepSelectionOnNextChange = false;
            return;
        }

        const availableIds = attributes.map((a) => a.id);
        const remaining = activeIds.value.filter((id) => availableIds.includes(id));

        if (remaining.length !== activeIds.value.length) {
            activeIds.value = remaining;
            notifySelectionChanged();
        }
    });

    function updateScrollBounds() {
        if (scroller == null) {
            return;
        }

        scroller.updateBounds();

        // a row that got shorter would otherwise stay dragged off to the side, showing nothing at its end
        const min = scroller.boundsX?.min ?? 0;
        if (DomUtil.translatePosition($(slidableElement.value!)).x < min) {
            scroller.scrollTo(min, 0);
        }
    }

    // called by the host right before it switches to a category the selection should follow into. the
    // watcher above consumes the flag, so it only ever covers that one change - the host has to raise it
    // for a switch that really changes the offered attributes, otherwise it would linger
    function keepSelection() {
        keepSelectionOnNextChange = true;
    }

    // called by the host for a switch that starts over. the watcher only drops what the new category
    // does not offer, so an attribute it happens to share would stay switched on without this
    function clearSelection() {
        keepSelectionOnNextChange = false;

        if (activeIds.value.length === 0) {
            return;
        }

        activeIds.value = [];
        notifySelectionChanged();
    }

    function attributeName(attribute: ICustomerArticleAttribute) {
        return Format.translate(attribute.name, clientLanguage()) ?? "";
    }

    function isActive(attribute: ICustomerArticleAttribute) {
        return activeIds.value.includes(attribute.id);
    }

    function toggle(attribute: ICustomerArticleAttribute) {
        // a drag ends with a click on the button under the pointer, which must not switch it
        if (scroller?.isDragging) {
            return;
        }

        if (isActive(attribute)) {
            activeIds.value = activeIds.value.filter((id) => id !== attribute.id);
        } else {
            activeIds.value = [...activeIds.value, attribute.id];
        }

        notifySelectionChanged();
    }

    // resolved against every filter attribute of the location, not just the ones this category offers: a
    // selection kept from another category stays applied while it is off the bar, and shows up switched
    // on again as soon as a category that does offer it is opened
    function notifySelectionChanged() {
        const active = filterAttributes.value.filter((a) => activeIds.value.includes(a.id));

        emit("selectionChanged", {
            includeIds: active.filter((a) => a.effect === AttributeEffect.Include).map((a) => a.id),
            excludeIds: active.filter((a) => a.effect === AttributeEffect.Exclude).map((a) => a.id),
        });
    }

    defineExpose({
        keepSelection,
        clearSelection,
    });
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";

    // the viewport the buttons are dragged in. it has to stay as wide as its host: MomentumScroller measures it while it
    // takes the row out of the flow, and a content-sized viewport would measure 0 then
    .article-attribute-filter {
        position: relative;
        overflow: hidden;
    }

    // single line, as wide as its buttons. updateBounds leaves it in the flow, so the width has to be stated or the row
    // would just fill the viewport and have nothing to drag. it takes the clicks itself, so a host can leave the empty
    // rest of the strip click-through
    .slidable {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px;
        width: max-content;
        padding: 10px 12px;
        pointer-events: auto;
    }

    .filter-attribute {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        border-radius: 999px;
        border: 1px solid colors.$greyBorder;
        background-color: #fff;
        color: colors.$lightGrey;
        font-size: 15px;
        line-height: 18px;
        white-space: nowrap;
        cursor: pointer;

        // include, active
        &.active {
            background-color: var(--colorPrimary);
            border-color: var(--colorPrimary);
            color: #fff;
        }

        // exclude, inactive
        &.exclude {
            border-color: colors.$red;
            color: colors.$red;

            // exclude, active
            &.active {
                background-color: colors.$red;
                border-color: colors.$red;
                color: #fff;
            }
        }
    }

    // bigger touch targets on the kiosk screen
    .kiosk-mode {
        // the kiosk bar is a row of its own above the list rather than something the list scrolls
        // behind, so unlike the app it keeps a surface and the line that divides it from the items
        .article-attribute-filter {
            background-color: #fff;
            border-bottom: 1px solid colors.$greyBorder;
        }

        .slidable {
            gap: 12px;
            padding: 14px 16px;
        }

        .filter-attribute {
            gap: 10px;
            padding: 14px 24px;
            font-size: 22px;
            line-height: 26px;
        }
    }
</style>
